import { MANAGED_AI_CONNECTOR_LABEL } from "../aiProviders/managedAiConnector";
import anthropicLogo from "./anthropic/logo.svg";
import bedrockLogo from "./awsBedrock/logo.svg";
import { awsBedrockUsage } from "./awsBedrock/usage";
import { baseUrlPattern, resolveBaseUrl } from "./baseUrl";
import {
  MANAGED_AI_CONNECTOR_TYPE,
  managedAiAuth,
  managedAiModelOptions,
  managedAiUsageHint,
} from "./managedAi";
import { MODELS_PREFIX } from "./models";
import { DEFAULT_OPENAI_BASE_URL } from "./openai/manifest";
import openaiLogo from "./openai/logo.svg";
import { openaiUsage } from "./openai/usage";
import logo from "./3bAi/logo.svg";
import { defineConnector } from "./types";

// The definitions behind MANAGED_AI_CONNECTOR_TYPE, which tenants create behind
// the managed-connectors flag, and MANAGED_AI_CONNECTORS, registered by
// identifier for Pony registration, env vars, and metering but kept out of the
// catalog because nobody creates them.
const skippedConnectionTest = {
  protocol: "http",
  request: () => ({ url: "" }),
} as const;

const openaiUrlPattern = () =>
  baseUrlPattern(undefined, DEFAULT_OPENAI_BASE_URL);

const bedrockUrlPattern = (configuration: Record<string, string>) =>
  `https://bedrock-runtime\\.${configuration.region || "[^/]*"}\\.amazonaws\\.com/.*`;

// Namespaced so a step's own OpenAI or AWS connector keeps its env vars, and
// its traffic never follows managed AI's endpoint. The registrant resolves the
// stack's regional base URL and Bedrock region into configuration.
const openaiEnvVars = (configuration: Record<string, string>) => ({
  MANAGED_OPENAI_BASE_URL: resolveBaseUrl(
    configuration.baseUrl,
    DEFAULT_OPENAI_BASE_URL
  ),
});

const bedrockEnvVars = (configuration: Record<string, string>) => ({
  MANAGED_BEDROCK_REGION: configuration.region ?? "",
});

export const managedAiConnectorType = defineConnector({
  identifier: MANAGED_AI_CONNECTOR_TYPE.identifier,
  name: MANAGED_AI_CONNECTOR_TYPE.name,
  tagLine: "AI via Anthropic and OpenAI",
  keywords: "anthropic claude bedrock openai gpt chatgpt llm",
  logo,
  protocol: "http",
  tags: ["ai", "managed"],
  // Listed only in the directory, for tenants with the managed-connectors flag.
  unlisted: true,
  defaultUrlPattern: (configuration: Record<string, string>) =>
    `(?:${openaiUrlPattern()}|${bedrockUrlPattern(configuration)})`,
  // Steps run with the per-provider connectors below, so this one never
  // reaches Pony with a credential.
  auth: {
    type: "reject",
    body: JSON.stringify({ error: "managed_ai_not_expanded" }),
  },
  // 3B sets the endpoint and credential, so the only settings are the name
  // and which managed models steps may call.
  customizeStepHandledInline: true,
  steps: [
    {
      title: "Managed by 3B",
      items: [
        {
          kind: "markdown",
          content: "Managed access to frontier AI models.",
        },
        {
          kind: "input",
          label: "Name",
          required: true,
          defaultValue: MANAGED_AI_CONNECTOR_TYPE.name,
          target: { kind: "connectedApp", field: "name" },
          helperText: "How this connector is displayed in 3B.",
        },
        {
          kind: "list",
          label: "Limit model access",
          prefix: MODELS_PREFIX,
          columns: [{ field: "name" }],
          addLabel: "Add model",
          options: managedAiModelOptions.map(({ name, provider }) =>
            provider === "openai"
              ? { name, detail: "OpenAI", logo: openaiLogo }
              : { name, detail: "Anthropic", logo: anthropicLogo }
          ),
          helperText:
            "When on, only selected models are allowed. When off, all models are allowed, including new ones.",
        },
      ],
    },
  ],
  connectionTest: skippedConnectionTest,
  publicEnvVars: (configuration: Record<string, string>) => ({
    ...openaiEnvVars(configuration),
    ...bedrockEnvVars(configuration),
  }),
  usageHint: (configuration: Record<string, string>) =>
    managedAiUsageHint(
      {
        bedrock:
          "Call the Bedrock runtime with the AWS SDK or CLI, passing process.env.MANAGED_BEDROCK_REGION as the region and placeholder credentials (any non-empty access key id and secret); the proxy re-signs each Bedrock request. Invoke a model by its geo inference profile id: anthropic.<model> with this region’s profile prefix (us., eu., or au.), for example us.anthropic.claude-opus-5-5.",
        openai:
          "Use process.env.MANAGED_OPENAI_BASE_URL as the OpenAI API base URL. Pass a placeholder API key; the proxy injects the real key. Call the Responses API (POST /responses under that base URL; with the AI SDK, openai(model) or openai.responses(model)). Chat Completions is blocked. Send store: false with every request. The AI SDK doesn’t detect gpt-6 models as reasoning models, so set providerOptions.openai.forceReasoning: true (ai 7.0.122 and @ai-sdk/openai 4.0.78 or later) before setting reasoningEffort.",
      },
      configuration
    ),
});

export const managedConnectorTypes = [
  defineConnector({
    identifier: "openaiManaged",
    name: "OpenAI",
    tagLine: MANAGED_AI_CONNECTOR_LABEL,
    logo: openaiLogo,
    protocol: "http",
    tags: ["ai"],
    defaultUrlPattern: openaiUrlPattern,
    auth: managedAiAuth("openai"),
    steps: [],
    connectionTest: skippedConnectionTest,
    publicEnvVars: openaiEnvVars,
    usage: openaiUsage(),
  }),
  defineConnector({
    identifier: "awsBedrockManaged",
    name: "AWS Bedrock",
    tagLine: MANAGED_AI_CONNECTOR_LABEL,
    logo: bedrockLogo,
    protocol: "http",
    tags: ["ai"],
    defaultUrlPattern: bedrockUrlPattern,
    auth: managedAiAuth("bedrock"),
    steps: [],
    connectionTest: skippedConnectionTest,
    publicEnvVars: bedrockEnvVars,
    usage: awsBedrockUsage(),
  }),
];
